export const fontFamily = "Montserrat, sans-serif";

export const fontWeights = {
  light: 300,
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extrabold: 800
} as const;

export type TypographyFontWeight = keyof typeof fontWeights;
export type TypographyTextTransform = "none" | "uppercase";

/** Figma transform labels: Normal (sentence case) or All Caps. */
export const typographyTransformLabels = {
  none: "Normal",
  uppercase: "All Caps"
} as const;

export const formatTypographyTransform = (
  textTransform: TypographyTextTransform
): string => typographyTransformLabels[textTransform];

export interface TypographyTokenDefinition {
  fontWeight: TypographyFontWeight;
  fontSize: number;
  textTransform: TypographyTextTransform;
  letterSpacing: string;
  lineHeightPx: number;
}

const pxToRem = (px: number): string => `${px / 16}rem`;

const toMuiTypographyStyle = (token: TypographyTokenDefinition) => ({
  fontSize: pxToRem(token.fontSize),
  fontWeight: fontWeights[token.fontWeight],
  letterSpacing: token.letterSpacing,
  lineHeight: token.lineHeightPx / token.fontSize,
  ...(token.textTransform === "uppercase"
    ? { textTransform: "uppercase" as const }
    : {})
});

/** Raw typography tokens from the MUI for Baana C2B Figma design system. */
export const typographyTokens = {
  h1: {
    fontWeight: "bold",
    fontSize: 36,
    textTransform: "none",
    letterSpacing: "0.5px",
    lineHeightPx: 50
  },
  h2: {
    fontWeight: "bold",
    fontSize: 26,
    textTransform: "none",
    letterSpacing: "0.5px",
    lineHeightPx: 36
  },
  h3: {
    fontWeight: "bold",
    fontSize: 20,
    textTransform: "none",
    letterSpacing: "0.5px",
    lineHeightPx: 28
  },
  h4: {
    fontWeight: "bold",
    fontSize: 18,
    textTransform: "none",
    letterSpacing: "0.5px",
    lineHeightPx: 25
  },
  h5: {
    fontWeight: "bold",
    fontSize: 16,
    textTransform: "none",
    letterSpacing: "0.5px",
    lineHeightPx: 22
  },
  h6: {
    fontWeight: "bold",
    fontSize: 14,
    textTransform: "none",
    letterSpacing: "0.5px",
    lineHeightPx: 20
  },
  subtitle1: {
    fontWeight: "semibold",
    fontSize: 20,
    textTransform: "none",
    letterSpacing: "0.5px",
    lineHeightPx: 28
  },
  subtitle2: {
    fontWeight: "semibold",
    fontSize: 16,
    textTransform: "none",
    letterSpacing: "0.5px",
    lineHeightPx: 22
  },
  subtitle3: {
    fontWeight: "semibold",
    fontSize: 14,
    textTransform: "none",
    letterSpacing: "0.5px",
    lineHeightPx: 20
  },
  body1: {
    fontWeight: "regular",
    fontSize: 16,
    textTransform: "none",
    letterSpacing: "0.5px",
    lineHeightPx: 24
  },
  body2: {
    fontWeight: "regular",
    fontSize: 14,
    textTransform: "none",
    letterSpacing: "0.5px",
    lineHeightPx: 21
  },
  buttonLarge: {
    fontWeight: "semibold",
    fontSize: 15,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    lineHeightPx: 21
  },
  buttonMedium: {
    fontWeight: "semibold",
    fontSize: 14,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    lineHeightPx: 20
  },
  buttonSmall: {
    fontWeight: "semibold",
    fontSize: 13,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    lineHeightPx: 18
  },
  caption: {
    fontWeight: "regular",
    fontSize: 12,
    textTransform: "none",
    letterSpacing: "0.4px",
    lineHeightPx: 18
  },
  overline: {
    fontWeight: "semibold",
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: "1px",
    lineHeightPx: 19
  },
  avatarLetter: {
    fontWeight: "regular",
    fontSize: 20,
    textTransform: "none",
    letterSpacing: "0.5px",
    lineHeightPx: 28
  },
  inputLabel: {
    fontWeight: "regular",
    fontSize: 12,
    textTransform: "none",
    letterSpacing: "0.25px",
    lineHeightPx: 12
  },
  helperText: {
    fontWeight: "regular",
    fontSize: 14,
    textTransform: "none",
    letterSpacing: "0.4px",
    lineHeightPx: 20
  },
  inputText: {
    fontWeight: "regular",
    fontSize: 16,
    textTransform: "none",
    letterSpacing: "0.75px",
    lineHeightPx: 24
  },
  tooltip: {
    fontWeight: "regular",
    fontSize: 10,
    textTransform: "none",
    letterSpacing: "0.25px",
    lineHeightPx: 14
  },
  link: {
    fontWeight: "medium",
    fontSize: 16,
    textTransform: "none",
    letterSpacing: "0.5px",
    lineHeightPx: 24
  }
} as const satisfies Record<string, TypographyTokenDefinition>;

/** Figma type-scale token names in design-system order. Style name = token key. */
export const typographyTokenNames = [
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "subtitle1",
  "subtitle2",
  "subtitle3",
  "body1",
  "body2",
  "buttonLarge",
  "buttonMedium",
  "buttonSmall",
  "caption",
  "helperText",
  "inputLabel",
  "inputText",
  "overline",
  "tooltip",
  "link"
] as const satisfies readonly (keyof typeof typographyTokens)[];

export type TypographyTokenName = (typeof typographyTokenNames)[number];

/**
 * Mobile font-size and line-height overrides for headings.
 * Source: Figma "Baana/Typography Breakpoints" (Mobile mode).
 * Desktop values remain in `typographyTokens`; only these properties change.
 */
export const typographyHeadingBreakpointTokens = {
  h1: {
    fontSize: 28,
    lineHeightPx: 40
  },
  h2: {
    fontSize: 22,
    lineHeightPx: 30
  },
  h3: {
    fontSize: 19,
    lineHeightPx: 24
  }
} as const satisfies Record<
  "h1" | "h2" | "h3",
  Pick<TypographyTokenDefinition, "fontSize" | "lineHeightPx">
>;

export const typographyHeadingBreakpointVariants = ["h1", "h2", "h3"] as const;

export type TypographyHeadingBreakpointVariant =
  (typeof typographyHeadingBreakpointVariants)[number];

interface ThemeBreakpoints {
  breakpoints: {
    down: (key: "sm") => string;
  };
}

/** Mobile heading overrides applied via `theme.breakpoints.down("sm")`. */
export const createHeadingBreakpointTypography = (
  theme: ThemeBreakpoints
): Record<TypographyHeadingBreakpointVariant, object> =>
  Object.fromEntries(
    typographyHeadingBreakpointVariants.map((variant) => {
      const mobile = typographyHeadingBreakpointTokens[variant];

      return [
        variant,
        {
          [theme.breakpoints.down("sm")]: {
            fontSize: pxToRem(mobile.fontSize),
            lineHeight: mobile.lineHeightPx / mobile.fontSize
          }
        }
      ];
    })
  ) as Record<TypographyHeadingBreakpointVariant, object>;

/** Component-only typography token (not in the Figma type scale). */
export const typographyComponentTokenNames = [
  "avatarLetter"
] as const satisfies readonly (keyof typeof typographyTokens)[];

/**
 * Vertical rhythm below headings — preserved from pre-token typography.
 * Figma tokens only cover typeface metrics; do not drop these when updating
 * `typographyTokens`. Override per-instance with `sx={{ mb: 0, pb: 0 }}`.
 */
const headingLayout = {
  h1: {
    padding: 0,
    paddingBottom: "1.5rem",
    marginBottom: "1rem",
    cursor: "default" as const
  },
  h2: {
    padding: 0,
    paddingBottom: "1.5rem",
    marginBottom: "1rem",
    cursor: "default" as const
  },
  h3: {
    padding: 0,
    paddingBottom: "1rem",
    marginBottom: "0.75rem",
    cursor: "default" as const
  },
  h4: {
    padding: 0,
    marginBottom: "0.75rem",
    cursor: "default" as const
  },
  h5: {
    padding: 0,
    marginBottom: "0.5rem",
    cursor: "default" as const
  },
  h6: {
    padding: 0,
    marginBottom: "0.5rem",
    cursor: "default" as const
  }
} as const;

/** MUI `createTheme()` typography config built from Figma tokens. */
export const typography = {
  fontSize: 16,
  htmlFontSize: 16,
  fontFamily,
  fontWeightLight: fontWeights.light,
  fontWeightRegular: fontWeights.regular,
  fontWeightMedium: fontWeights.medium,
  fontWeightSemibold: fontWeights.semibold,
  fontWeightBold: fontWeights.bold,
  fontWeightExtrabold: fontWeights.extrabold,
  h1: {
    ...toMuiTypographyStyle(typographyTokens.h1),
    ...headingLayout.h1
  },
  h2: {
    ...toMuiTypographyStyle(typographyTokens.h2),
    ...headingLayout.h2
  },
  h3: {
    ...toMuiTypographyStyle(typographyTokens.h3),
    ...headingLayout.h3
  },
  h4: {
    ...toMuiTypographyStyle(typographyTokens.h4),
    ...headingLayout.h4
  },
  h5: {
    ...toMuiTypographyStyle(typographyTokens.h5),
    ...headingLayout.h5
  },
  h6: {
    ...toMuiTypographyStyle(typographyTokens.h6),
    ...headingLayout.h6
  },
  subtitle1: toMuiTypographyStyle(typographyTokens.subtitle1),
  subtitle2: toMuiTypographyStyle(typographyTokens.subtitle2),
  subtitle3: toMuiTypographyStyle(typographyTokens.subtitle3),
  body1: toMuiTypographyStyle(typographyTokens.body1),
  body2: toMuiTypographyStyle(typographyTokens.body2),
  // MUI Button reads `typography.button` internally. Keep prod baseline so
  // header/modal buttons match production; use buttonMedium/Large/Small explicitly.
  button: {
    fontSize: "0.8rem",
    fontWeight: fontWeights.bold
  },
  buttonLarge: toMuiTypographyStyle(typographyTokens.buttonLarge),
  buttonMedium: toMuiTypographyStyle(typographyTokens.buttonMedium),
  buttonSmall: toMuiTypographyStyle(typographyTokens.buttonSmall),
  caption: toMuiTypographyStyle(typographyTokens.caption),
  overline: toMuiTypographyStyle(typographyTokens.overline),
  avatarLetter: toMuiTypographyStyle(typographyTokens.avatarLetter),
  inputLabel: toMuiTypographyStyle(typographyTokens.inputLabel),
  helperText: toMuiTypographyStyle(typographyTokens.helperText),
  inputText: toMuiTypographyStyle(typographyTokens.inputText),
  tooltip: toMuiTypographyStyle(typographyTokens.tooltip),
  link: toMuiTypographyStyle(typographyTokens.link)
};
